// src/pages/admin/OdesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOrdersNormalized, type OC } from "../../services/api";

const money = (n?: number) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
    : "—";

const dateMX = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const OdesPage: React.FC = () => {
  const navigate = useNavigate();

  const [ocs, setOcs] = useState<OC[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<"fecha" | "folio" | "monto" | "gastos">("folio");
  const [filterRentabilidad, setFilterRentabilidad] =
    useState<"todas" | "alerta" | "noRentable">("todas");

  /** ===== Carga de datos Normalizada ===== */
  useEffect(() => {
    const fetchOdes = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        // Usamos la versión normalizada que ya calcula totales y limpia folios
        const data = await getOrdersNormalized();
        setOcs(data);
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Error al cargar órdenes";
        setErrorMsg(`❌ ${msg}`);
      } finally {
        setLoading(false);
      }
    };
    fetchOdes();
  }, []);

  /** ===== Filtrar + Ordenar ===== */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let base = q
      ? ocs.filter((o) =>
          [o.folio, o.customerName, o.plate, String(o.id)]
            .filter(Boolean)
            .some((s) => s.toLowerCase().includes(q))
        )
      : [...ocs];

    // Filtro de rentabilidad mejorado
    base = base.filter((o) => {
      if (filterRentabilidad === "todas") return true;
      if (o.presupuestoTotal <= 0) return false;
      const porcentaje = (o.totalEstimado / o.presupuestoTotal) * 100;
      
      if (filterRentabilidad === "alerta") return porcentaje > 33 && porcentaje <= 100;
      if (filterRentabilidad === "noRentable") return porcentaje > 100;
      return true;
    });

    // Ordenamiento
    base.sort((a, b) => {
      switch (sortKey) {
        case "fecha":
          return (new Date(b.created || 0).getTime()) - (new Date(a.created || 0).getTime());
        case "monto":
          return b.presupuestoTotal - a.presupuestoTotal;
        case "gastos":
          return b.totalEstimado - a.totalEstimado;
        default:
          return a.folio.localeCompare(b.folio);
      }
    });

    return base;
  }, [ocs, query, sortKey, filterRentabilidad]);

  return (
    <div className="w-full px-4 sm:px-6 pt-24 pb-10 bg-slate-50 min-h-screen">
      {/* HERO SECTION */}
      <header className="mx-auto max-w-[1200px] mb-6">
        <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-6 shadow-xl text-white">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Órdenes de Compra</h2>
              <p className="text-slate-400 text-sm mt-1">Gestión de presupuestos y gastos operativos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-lg px-4 py-2 border border-white/10">
              <span className="text-xs uppercase tracking-wider opacity-60 block">Registros</span>
              <span className="text-2xl font-mono font-bold">{ocs.length}</span>
            </div>
          </div>
        </div>
      </header>

      {/* TOOLBAR */}
      <div className="sticky top-20 z-30 mx-auto max-w-[1200px] mb-8">
        <div className="rounded-xl border border-slate-200 bg-white/80 backdrop-blur-md p-4 shadow-sm flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[280px]">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por folio, cliente o placa..."
              className="w-full rounded-lg border border-slate-200 px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
            />
          </div>

          <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
            {(['todas', 'alerta', 'noRentable'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterRentabilidad(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  filterRentabilidad === f 
                    ? "bg-white text-indigo-600 shadow-sm" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {f === 'todas' ? 'Todas' : f === 'alerta' ? '⚠️ Alerta' : '❌ Críticas'}
              </button>
            ))}
          </div>

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as any)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none"
          >
            <option value="folio">Ordenar por Folio</option>
            <option value="fecha">Más Recientes</option>
            <option value="monto">Mayor Presupuesto</option>
            <option value="gastos">Mayor Gasto</option>
          </select>
        </div>
      </div>

      {/* MAIN LIST */}
      <main className="mx-auto max-w-[1200px]">
        {errorMsg && (
          <div className="p-4 mb-6 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => <div key={i} className="h-64 bg-slate-200 animate-pulse rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
            <p className="text-slate-500">No se encontraron órdenes de compra</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((o) => {
              const pct = o.presupuestoTotal > 0 ? (o.totalEstimado / o.presupuestoTotal) * 100 : 0;
              const isOver = pct > 100;
              const isWarning = pct > 33;

              return (
                <div key={o.id} className="group relative bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all">
                  <div className={`absolute top-0 left-0 w-1 h-full rounded-l-2xl ${isOver ? 'bg-rose-500' : isWarning ? 'bg-amber-500' : 'bg-indigo-500'}`} />
                  
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ID: {o.id}</span>
                      <h3 className="text-lg font-bold text-slate-800 leading-tight">{o.folio}</h3>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm ${
                      o.statusName.includes('Recibido') ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {o.statusName}
                    </span>
                  </div>

                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Cliente:</span>
                      <span className="font-medium text-slate-700">{o.customerName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Unidad:</span>
                      <span className="font-medium text-slate-700">{o.plate || 'Sin placa'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Fecha:</span>
                      <span className="font-medium text-slate-700">{dateMX(o.created)}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 mb-5">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">Gastado vs Presupuesto</p>
                        <p className="text-xl font-black text-slate-900">{money(o.totalEstimado)}</p>
                      </div>
                      <p className="text-sm font-bold text-slate-500">de {money(o.presupuestoTotal)}</p>
                    </div>
                    
                    <div className="relative h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-1000 ${isOver ? 'bg-rose-500' : isWarning ? 'bg-amber-500' : 'bg-indigo-500'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className={`text-[11px] font-bold ${isOver ? 'text-rose-600' : 'text-slate-500'}`}>
                        {pct.toFixed(1)}% utilizado
                      </span>
                      {isOver && <span className="text-[11px] font-bold text-rose-600 animate-pulse">EXCEDIDO</span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => navigate(`/admin/gastos?odeSId=${o.id}`)}
                      className="flex flex-col items-center justify-center p-2 rounded-lg border border-indigo-100 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                    >
                      <span className="text-lg">➕</span>
                      <span className="text-[10px] font-bold uppercase">Gasto</span>
                    </button>
                    <button
                      onClick={() => navigate(`/admin/odes/${o.id}`)}
                      className="flex flex-col items-center justify-center p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-lg">🔎</span>
                      <span className="text-[10px] font-bold uppercase">Detalle</span>
                    </button>
                    <button
                      disabled
                      className="flex flex-col items-center justify-center p-2 rounded-lg border border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                    >
                      <span className="text-lg">💵</span>
                      <span className="text-[10px] font-bold uppercase">Cobrar</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default OdesPage;